import * as bitcoin from 'bitcoinjs-lib'
import type { Taptree } from 'bitcoinjs-lib/src/types'
import { SecurePurchaseUnavailableError, type SecurePurchaseCapabilities } from './secure-purchase'

/**
 * The Ordinals Wallet co-signer key for passthrough v4, pinned in the build.
 *
 * Every protected listing's escrow is `tr(seller, multi_a(2, seller, C))`
 * with this `C`. The capabilities endpoint also publishes it, but the client
 * must not take the API's word for it: an attacker holding the API could
 * publish their own key and every new listing would commit to an escrow only
 * they can co-sign. With the pin a compromised API can only break protected
 * listings. Rotating `C` therefore requires a client release; the API cannot
 * rotate on its own.
 *
 * The seller's own key is required on the sale leaf and is alone on the
 * recovery leaf, so this key can never move an asset by itself.
 */
export const PINNED_COSIGNER_XONLY_HEX = '1d08b7c71f6f1e97a0a4cf005db7a977c85e34652a0c9365842aee25997c7dee'

/**
 * BIP-341's provably unspendable internal key. The escrow has no key path:
 * wallet libraries (scure) tweak a signing key that equals the internal key
 * before searching the leaf for it, and would sign the key path instead of
 * the 2-of-2 leaf if the seller key sat there.
 */
export const NUMS_INTERNAL_KEY_HEX = '50929b74c1a04954b78b4b6035e97a5e078a5a0f28ec96d547bfee9ace803ac0'

/** Blocks after the escrow confirms before the seller alone can reclaim it. */
const RECOVERY_DELAY_BLOCKS = 144

export const TAPSCRIPT_LEAF_VERSION = 0xc0

const XONLY_RE = /^[0-9a-f]{64}$/i

export function toXOnly(publicKeyHex: string): Buffer {
	const hex = publicKeyHex.trim().toLowerCase()
	if (XONLY_RE.test(hex)) return Buffer.from(hex, 'hex')
	if (/^0[23][0-9a-f]{64}$/.test(hex)) return Buffer.from(hex.slice(2), 'hex')
	throw new SecurePurchaseUnavailableError('invalid_public_key', 'Public key must be 32 or 33 bytes of hex')
}

/** Refuse capabilities that publish any co-signer key but the pinned one. */
export function requirePinnedCosigner(caps: SecurePurchaseCapabilities): Buffer {
	const published = String(caps.cosigner_public_key || '').toLowerCase()
	if (!XONLY_RE.test(published)) {
		throw new SecurePurchaseUnavailableError(
			'cosigner_key_unavailable',
			'Ordinals Wallet did not publish its co-signer key; refusing to build a protected transaction'
		)
	}
	if (published !== PINNED_COSIGNER_XONLY_HEX) {
		throw new SecurePurchaseUnavailableError(
			'cosigner_key_unpinned',
			'The published co-signer key does not match the key built into this client; refusing to build a protected transaction'
		)
	}
	return Buffer.from(PINNED_COSIGNER_XONLY_HEX, 'hex')
}

type PassthroughEscrow = {
	/** Sale leaf: `<S> OP_CHECKSIG <C> OP_CHECKSIGADD OP_2 OP_NUMEQUAL` */
	leaf: Buffer
	/** Recovery leaf: `<144> OP_CHECKSEQUENCEVERIFY OP_DROP <S> OP_CHECKSIG` */
	recoveryLeaf: Buffer
	/** P2TR scriptPubKey of `tr(NUMS, {leaf, recoveryLeaf})` */
	output: Buffer
	address: string
	/** Control block for the sale leaf. */
	controlBlock: Buffer
	/** Control block for the recovery leaf. */
	recoveryControlBlock: Buffer
	internalKey: Buffer
	sellerXOnly: Buffer
	cosignerXOnly: Buffer
}

/** `<144> CSV DROP <S> CHECKSIG`: the seller's unilateral reclaim leaf. */
function recoveryLeafFor(sellerXOnly: Buffer): Buffer {
	return bitcoin.script.compile([
		bitcoin.script.number.encode(RECOVERY_DELAY_BLOCKS),
		bitcoin.opcodes.OP_CHECKSEQUENCEVERIFY,
		bitcoin.opcodes.OP_DROP,
		sellerXOnly,
		bitcoin.opcodes.OP_CHECKSIG
	])
}

/** Rebuild a listing's escrow locally from the seller key and the pinned co-signer. */
export function passthroughEscrow(sellerXOnly: Buffer, cosignerXOnly: Buffer = Buffer.from(PINNED_COSIGNER_XONLY_HEX, 'hex')): PassthroughEscrow {
	if (sellerXOnly.length !== 32 || cosignerXOnly.length !== 32) {
		throw new SecurePurchaseUnavailableError('invalid_public_key', 'Escrow keys must be x-only')
	}
	if (sellerXOnly.equals(cosignerXOnly)) {
		throw new SecurePurchaseUnavailableError('escrow_keys_identical', 'Seller and co-signer keys must be distinct')
	}
	const leaf = bitcoin.script.compile([
		sellerXOnly,
		bitcoin.opcodes.OP_CHECKSIG,
		cosignerXOnly,
		bitcoin.opcodes.OP_CHECKSIGADD,
		bitcoin.opcodes.OP_2,
		bitcoin.opcodes.OP_NUMEQUAL
	])
	const recoveryLeaf = recoveryLeafFor(sellerXOnly)
	const internalKey = Buffer.from(NUMS_INTERNAL_KEY_HEX, 'hex')
	const scriptTree: Taptree = [{ output: leaf }, { output: recoveryLeaf }]
	let payment: bitcoin.payments.Payment
	let recovery: bitcoin.payments.Payment
	try {
		payment = bitcoin.payments.p2tr({
			internalPubkey: internalKey,
			scriptTree,
			redeem: { output: leaf, redeemVersion: TAPSCRIPT_LEAF_VERSION },
			network: bitcoin.networks.bitcoin
		})
		recovery = bitcoin.payments.p2tr({
			internalPubkey: internalKey,
			scriptTree,
			redeem: { output: recoveryLeaf, redeemVersion: TAPSCRIPT_LEAF_VERSION },
			network: bitcoin.networks.bitcoin
		})
	} catch {
		throw new SecurePurchaseUnavailableError('invalid_public_key', 'Seller key is not a valid Taproot key')
	}
	const witness = payment.witness
	const recoveryWitness = recovery.witness
	if (!payment.output || !payment.address || !witness || witness.length < 2 || !recoveryWitness || recoveryWitness.length < 2) {
		throw new SecurePurchaseUnavailableError('escrow_construction_failed', 'Could not build the passthrough escrow')
	}
	return {
		leaf,
		recoveryLeaf,
		output: Buffer.from(payment.output),
		address: payment.address,
		controlBlock: Buffer.from(witness[witness.length - 1]),
		recoveryControlBlock: Buffer.from(recoveryWitness[recoveryWitness.length - 1]),
		internalKey,
		sellerXOnly,
		cosignerXOnly
	}
}

/** Both x-only keys of a `multi_a(2, S, C)` leaf, or null if the script is not one. */
export function parsePassthroughLeaf(leaf: Buffer | Uint8Array): { seller: Buffer; cosigner: Buffer } | null {
	const chunks = bitcoin.script.decompile(Buffer.from(leaf))
	if (!chunks || chunks.length !== 6) return null
	const [seller, checksig, cosigner, add, two, numequal] = chunks
	if (
		!Buffer.isBuffer(seller) ||
		seller.length !== 32 ||
		checksig !== bitcoin.opcodes.OP_CHECKSIG ||
		!Buffer.isBuffer(cosigner) ||
		cosigner.length !== 32 ||
		add !== bitcoin.opcodes.OP_CHECKSIGADD ||
		two !== bitcoin.opcodes.OP_2 ||
		numequal !== bitcoin.opcodes.OP_NUMEQUAL
	) {
		return null
	}
	return { seller, cosigner }
}
